/**
 * Duel invite link system — create and accept shareable invite codes.
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import { notifyDuelAccepted } from '@/lib/notificationTriggers';
import { checkRateLimit } from '@/lib/rateLimiter';

const log = createLogger('DUEL_INVITES');
const WEB_BASE = 'https://douevenlift.com';

function generateCode(length = 8): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let code = '';
  for (let i = 0; i < length; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

export interface DuelInvite {
  id: string;
  duel_id: string;
  code: string;
  challenger_id: string;
  accepted_by: string | null;
  expires_at: string;
  created_at: string;
}

/**
 * Create a duel invite and return the shareable URL.
 */
export async function createDuelInvite(
  duelId: string,
  challengerId: string,
): Promise<{ url: string; code: string; invite: DuelInvite }> {
  const rateCheck = await checkRateLimit('duel_create');
  if (!rateCheck.allowed) {
    throw new Error('Too many duels created. Try again later.');
  }

  const code = generateCode();

  const { data, error } = await supabase
    .from('duel_invites')
    .insert({
      duel_id: duelId,
      code,
      challenger_id: challengerId,
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    })
    .select()
    .single();

  if (error) {
    log.error('Failed to create duel invite:', error);
    throw error;
  }

  const url = `${WEB_BASE}/duel/invite/${code}`;
  return { url, code, invite: data as DuelInvite };
}

/**
 * Accept a duel invite by code. Sets accepted_by and activates the duel.
 */
export async function acceptDuelInvite(
  code: string,
  userId: string,
): Promise<{ duelId: string; invite: DuelInvite }> {
  // Look up the invite
  const { data: invite, error: lookupError } = await supabase
    .from('duel_invites')
    .select('*')
    .eq('code', code)
    .single();

  if (lookupError || !invite) {
    throw new Error('Invite not found or expired');
  }

  const inv = invite as DuelInvite;

  if (inv.accepted_by) {
    throw new Error('This invite has already been accepted');
  }

  if (new Date(inv.expires_at) < new Date()) {
    throw new Error('This invite has expired');
  }

  if (inv.challenger_id === userId) {
    throw new Error('You cannot accept your own invite');
  }

  // Anti-farming: block excessive repeat duels between same pair
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const { data: recentDuels } = await supabase
    .from('duels')
    .select('id')
    .or(`and(challenger_id.eq.${inv.challenger_id},challenged_id.eq.${userId}),and(challenger_id.eq.${userId},challenged_id.eq.${inv.challenger_id})`)
    .gte('created_at', thirtyDaysAgo);

  if (recentDuels && recentDuels.length >= 3) {
    throw new Error('You\'ve dueled this opponent too recently. Challenge someone new to keep rankings fair!');
  }

  // Mark invite as accepted
  const { error: updateInviteError } = await supabase
    .from('duel_invites')
    .update({ accepted_by: userId })
    .eq('id', inv.id);

  if (updateInviteError) {
    log.error('Failed to update invite:', updateInviteError);
    throw updateInviteError;
  }

  // Set the challenged_id on the duel and activate it
  const { error: updateDuelError } = await supabase
    .from('duels')
    .update({
      challenged_id: userId,
      opponent_id: userId,
      status: 'active',
    })
    .eq('id', inv.duel_id);

  if (updateDuelError) {
    log.error('Failed to activate duel:', updateDuelError);
    throw updateDuelError;
  }

  // Notify the challenger that their duel was accepted
  // We don't have the acceptor's username here, so fetch it
  try {
    const { data: acceptorProfile } = await supabase
      .from('profiles')
      .select('handle, display_name')
      .eq('id', userId)
      .single();
    const acceptorName = acceptorProfile?.handle || acceptorProfile?.display_name || 'Someone';
    notifyDuelAccepted(inv.challenger_id, acceptorName, inv.duel_id).catch(() => {});
  } catch {
    // Non-blocking — don't fail the accept flow
  }

  return { duelId: inv.duel_id, invite: inv };
}

/**
 * Look up an invite by code (for the deep link handler).
 */
export async function getDuelInviteByCode(code: string): Promise<DuelInvite | null> {
  const { data, error } = await supabase
    .from('duel_invites')
    .select('*')
    .eq('code', code)
    .single();

  if (error) return null;
  return data as DuelInvite;
}
